// Thin wrapper around the Web Speech Synthesis API so the UI can read a
// description aloud in any of the platform's 12 languages. All state is
// kept in this module so multiple components don't race each other —
// starting a new utterance automatically cancels any currently-speaking
// one.
//
// 2026-05-25 — widened from en/hi to all 12 supported languages so the
// PWA install prompt (and any future reader) can speak in the user's
// chosen language. Existing callers passing 'en'/'hi' are unaffected
// (subset of the union).

export type TtsLang =
  | 'en' | 'hi' | 'bn' | 'mr' | 'ta' | 'te'
  | 'gu' | 'ur' | 'kn' | 'or' | 'pa' | 'ml';

// Our language code → ordered BCP-47 voice tags to try (most specific
// first). Indian locales preferred; generic fallbacks last. Voice
// availability is device-dependent — if none match, we still set
// utterance.lang so the OS can pick its best default for that language.
const BCP47: Record<TtsLang, string[]> = {
  en: ['en-IN', 'en-US', 'en-GB', 'en'],
  hi: ['hi-IN', 'hi'],
  bn: ['bn-IN', 'bn-BD', 'bn'],
  mr: ['mr-IN', 'mr'],
  ta: ['ta-IN', 'ta-LK', 'ta'],
  te: ['te-IN', 'te'],
  gu: ['gu-IN', 'gu'],
  ur: ['ur-IN', 'ur-PK', 'ur'],
  kn: ['kn-IN', 'kn'],
  or: ['or-IN', 'or'],
  pa: ['pa-IN', 'pa-Guru-IN', 'pa'],
  ml: ['ml-IN', 'ml'],
};

// Primary lang tag set on the utterance (drives OS voice selection even
// when getVoices() is empty, which is common on first paint / iOS).
function primaryTag(lang: TtsLang): string {
  return (BCP47[lang] && BCP47[lang][0]) || 'en-IN';
}

export function isTtsSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

// Voice lists are populated asynchronously on some browsers. We cache
// the latest list and refresh on the `voiceschanged` event.
let cachedVoices: SpeechSynthesisVoice[] = [];
if (isTtsSupported()) {
  const refresh = () => { cachedVoices = window.speechSynthesis.getVoices(); };
  refresh();
  window.speechSynthesis.addEventListener?.('voiceschanged', refresh);
}

function pickVoice(lang: TtsLang): SpeechSynthesisVoice | null {
  // Always re-read in case `voiceschanged` hasn't fired yet.
  if (!cachedVoices.length) cachedVoices = window.speechSynthesis.getVoices();
  const targets = BCP47[lang] || BCP47.en;
  // Exact tag match first (e.g. 'ta-IN').
  for (const tag of targets) {
    const exact = cachedVoices.find((v) => v.lang === tag);
    if (exact) return exact;
  }
  // Prefix match ("ta" matches any Tamil locale, "en" any English, etc.).
  const prefix = (targets[0] || 'en').split('-')[0].toLowerCase();
  return cachedVoices.find((v) => v.lang?.toLowerCase().startsWith(prefix)) || null;
}

// Observable "is speaking?" state with a tiny subscriber list. React
// components subscribe via useTtsState() to update their icon state.
type Listener = (speaking: boolean) => void;
const listeners = new Set<Listener>();
let speaking = false;
function setSpeaking(v: boolean) {
  if (speaking === v) return;
  speaking = v;
  listeners.forEach((l) => l(v));
}

export function onTtsStateChange(l: Listener): () => void {
  listeners.add(l);
  l(speaking);
  return () => { listeners.delete(l); };
}

export function isSpeaking(): boolean { return speaking; }

export function stopTts(): void {
  if (!isTtsSupported()) return;
  window.speechSynthesis.cancel();
  setSpeaking(false);
}

// Returns true if speech started, false if TTS isn't available.
export function speak(text: string, lang: TtsLang): boolean {
  if (!isTtsSupported() || !text.trim()) return false;
  // Only cancel if something is actually playing/queued. An unconditional
  // cancel() right before speak() is a known Chrome cause of a delayed
  // (or missed) onstart — skipping it on a fresh press makes the first
  // utterance start noticeably sooner.
  if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
    window.speechSynthesis.cancel();
  }

  const u = new SpeechSynthesisUtterance(text);
  u.lang = primaryTag(lang);
  // Slightly slower than default feels less hurried + clearer for
  // step-by-step instructions in a second language.
  u.rate = 0.92;
  u.pitch = 1.0;

  const voice = pickVoice(lang);
  if (voice) u.voice = voice;

  u.onstart = () => setSpeaking(true);
  u.onend   = () => setSpeaking(false);
  u.onerror = () => setSpeaking(false);

  window.speechSynthesis.speak(u);
  return true;
}
